/**
 * Single point of access to build-time configuration.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, so a missing variable
 * silently becomes `undefined` and only surfaces much later as an opaque
 * Firebase or network error. Everything required is therefore validated here,
 * once, and the module throws immediately when something is missing.
 */

interface FirebaseEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export interface Env {
  apiBaseUrl: string;
  firebase: FirebaseEnv;
}

const missing: string[] = [];

// vite/client declares ImportMetaEnv with a string index signature, so a bare
// `keyof` widens to `string | number`. Intersecting with string keeps the
// autocomplete over the declared VITE_* names without the numeric half.
function required(name: keyof ImportMetaEnv & string, value: string | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    missing.push(name);
    return '';
  }
  return trimmed;
}

const firebase: FirebaseEnv = {
  apiKey: required('VITE_FIREBASE_API_KEY', import.meta.env.VITE_FIREBASE_API_KEY),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN', import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: required('VITE_FIREBASE_PROJECT_ID', import.meta.env.VITE_FIREBASE_PROJECT_ID),
  storageBucket: required(
    'VITE_FIREBASE_STORAGE_BUCKET',
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  ),
  messagingSenderId: required(
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  ),
  appId: required('VITE_FIREBASE_APP_ID', import.meta.env.VITE_FIREBASE_APP_ID),
};

if (missing.length > 0) {
  throw new Error(
    `Missing required environment variables: ${missing.join(', ')}. ` +
      'Copy .env.example to .env and fill it in.',
  );
}

export const env: Env = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL?.trim() || '/api',
  firebase,
};
